import React, { useEffect, useState, useRef } from "react";
import QRCode from "react-qr-code";
import axios from "../axiosInstance";
import {
  ContentCopy,
  Download,
  Info,
  Smartphone,
  ShoppingBag,
  Security,
  QrCode2,
  Link as LinkIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import { useLocation, Link } from "react-router-dom";

const GenerateSellerQR = ({ sellerId }) => {
  const [qrValue, setQrValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    const fetchKioskCategory = async () => {
      try {
        const res = await axios.get("/api/v1/buyer-categories?page=1&limit=10");

        const kiosk = res.data.data.find(
          (cat) => cat.name?.toLowerCase() === "kiosk",
        );

        if (!kiosk) {
          alert("Kiosk buyer category not found");
          return;
        }

        const qrUrl = `${window.location.origin}/scan?sellerId=${sellerId}&buyerCategoryId=${kiosk._id}`;
        setQrValue(qrUrl);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchKioskCategory();
  }, [sellerId]);

  const copyLink = () => {
    navigator.clipboard.writeText(qrValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const svg = qrRef.current.querySelector("svg");
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const png = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = png;
      link.download = `kiosk-qr-${sellerId}.png`;
      link.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgStr);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Generating QR Code...</p>
      </div>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#f8fafc",
          py: { xs: 1, md: 2 },
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
           
            width: "fit-content",
            backgroundColor: "#fff",
            ml: { xs: 1, sm: 2.9 },
            padding: "13px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/seller-dashboard/pending-invoices"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Pending Invoices
          </MUILink>
          <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
            Kiosk Consumer
          </Typography>
        </Breadcrumbs>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
          {/* Header Section */}
          <Box style={styles.header}>
            <Box style={styles.headerIcon}>
              <QrCode2 sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <h2 style={styles.title}>Kiosk Consumer QR Code</h2>
              <p style={styles.subtitle}>
                Generate and share this QR code for your Kiosk customers
              </p>
            </Box>
          </Box>

          <Box style={styles.divider}></Box>

          <Box style={styles.content}>
            {/* QR Code Section */}
            <Box style={styles.qrSection}>
              <Box style={styles.qrContainer} ref={qrRef}>
                <Box style={styles.qrBackground}>
                  <QRCode
                    value={qrValue}
                    size={220}
                    fgColor="#1a365d"
                    bgColor="transparent"
                  />
                  <Box style={styles.qrOverlay}>
                    <QrCode2 sx={{ fontSize: 40, color: "rgba(0,0,0,0.1)" }} />
                  </Box>
                </Box>
                <Box style={styles.qrLabel}>Scan to Browse Products</Box>
              </Box>

              <button
                onClick={downloadQR}
                style={styles.downloadButton}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "translateY(-2px)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "translateY(0)")
                }
              >
                <Download sx={{ fontSize: 20 }} />
                Download QR Code
              </button>
            </Box>

            {/* Link & Information Section */}
            <Box style={styles.infoSection}>
              {/* Direct Link */}
              <Box style={styles.linkSection}>
                <Box style={styles.sectionHeader}>
                  <LinkIcon sx={{ fontSize: 20, color: "#0b4064" }} />
                  <label style={styles.sectionLabel}>Direct Scan Link</label>
                </Box>
                <Box style={styles.linkContainer}>
                  <Box style={styles.linkBox}>
                    <code style={styles.linkText}>{qrValue}</code>
                  </Box>
                  <button
                    onClick={copyLink}
                    style={{
                      ...styles.copyButton,
                      background: copied ? "#059669" : "#0b4064",
                    }}
                    onMouseEnter={(e) => {
                      if (!copied) {
                        e.currentTarget.style.background = "#0d6efd";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!copied) {
                        e.currentTarget.style.background = "#0b4064";
                      }
                    }}
                  >
                    <ContentCopy sx={{ fontSize: 20 }} />
                    {copied ? "Copied!" : "Copy Link"}
                  </button>
                </Box>
              </Box>

              {/* Usage Instructions */}
              <Box style={styles.instructions}>
                <Box style={styles.instructionsHeader}>
                  <Info sx={{ fontSize: 24, color: "#0c4a6e" }} />
                  <h3 style={styles.instructionsTitle}>
                    How to Use This QR Code
                  </h3>
                </Box>

                <Box style={styles.featureGrid}>
                  <Box style={styles.featureCard}>
                    <Box style={styles.featureIcon}>
                      <Smartphone sx={{ fontSize: 24 }} />
                    </Box>
                    <h4 style={styles.featureTitle}>Share & Display</h4>
                    <p style={styles.featureDescription}>
                      Share via WhatsApp, email, or print for banners, standees,
                      and POS displays
                    </p>
                  </Box>

                  <Box style={styles.featureCard}>
                    <Box style={styles.featureIcon}>
                      <ShoppingBag sx={{ fontSize: 24 }} />
                    </Box>
                    <h4 style={styles.featureTitle}>Direct Ordering</h4>
                    <p style={styles.featureDescription}>
                      Customers can scan, browse products, and place orders
                      directly
                    </p>
                  </Box>

                  <Box style={styles.featureCard}>
                    <Box style={styles.featureIcon}>
                      <Security sx={{ fontSize: 24 }} />
                    </Box>
                    <h4 style={styles.featureTitle}>Catalog Management</h4>
                    <p style={styles.featureDescription}>
                      Control what customers see through the Catalog section
                    </p>
                  </Box>
                </Box>

                {/* Management Features */}
                <Box style={styles.managementSection}>
                  <h4 style={styles.managementTitle}>
                    📋 Manage in Catalog Section:
                  </h4>
                  <Box style={styles.featureList}>
                    <Box style={styles.featureItem}>
                      <span style={styles.checkmark}>✓</span> Control prices and
                      discounts
                    </Box>
                    <Box style={styles.featureItem}>
                      <span style={styles.checkmark}>✓</span> Hide or show
                      specific products
                    </Box>
                    <Box style={styles.featureItem}>
                      <span style={styles.checkmark}>✓</span> Limit items
                      displayed
                    </Box>
                    <Box style={styles.featureItem}>
                      <span style={styles.checkmark}>✓</span> Customize product
                      visibility
                    </Box>
                    <Box style={styles.featureItem}>
                      <span style={styles.checkmark}>✓</span> Update catalog in
                      real-time
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const styles = {
  
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #0b4064",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginTop: "16px",
    color: "#64748b",
    fontSize: "16px",
    fontWeight: "500",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "20px",
  },
  headerIcon: {
    background: "linear-gradient(135deg, #0b4064 0%, #1e88e5 100%)",
    padding: "14px",
    borderRadius: "14px",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    margin: "0",
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a365d",
    lineHeight: "1.2",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: "15px",
    color: "#64748b",
    fontWeight: "400",
  },
  divider: {
    height: "1px",
    background: "linear-gradient(90deg, transparent, #e2e8f0, transparent)",
    margin: "24px 0",
  },
  content: {
    display: "flex",
    gap: "32px",
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  qrSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
    minWidth: "280px",
  },
  qrContainer: {
    textAlign: "center",
  },
  qrBackground: {
    background: "white",
    padding: "24px",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(11, 64, 100, 0.1)",
    border: "1px solid #e2e8f0",
    marginBottom: "12px",
    position: "relative",
  },
  qrOverlay: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  qrLabel: {
    fontSize: "14px",
    color: "#64748b",
    fontWeight: "500",
    marginTop: "8px",
  },
  downloadButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "14px 28px",
    borderRadius: "10px",
    border: "none",
    background: "linear-gradient(135deg, #0b4064 0%, #1e88e5 100%)",
    color: "white",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    width: "100%",
    boxShadow: "0 4px 12px rgba(11, 64, 100, 0.2)",
  },
  infoSection: {
    flex: "1",
    minWidth: "300px",
  },
  linkSection: {
    marginBottom: "28px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  sectionLabel: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  linkContainer: {
    display: "flex",
    gap: "12px",
  },
  linkBox: {
    flex: "1",
    padding: "14px 18px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  linkText: {
    color: "#1e293b",
    fontFamily: "'Roboto Mono', monospace",
  },
  copyButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: "14px 24px",
    borderRadius: "10px",
    border: "none",
    background: "#0b4064",
    color: "white",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "600",
    transition: "all 0.3s ease",
    minWidth: "140px",
    boxShadow: "0 4px 12px rgba(11, 64, 100, 0.2)",
  },
  instructions: {
    background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid #bae6fd",
  },
  instructionsHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
  instructionsTitle: {
    margin: "0",
    fontSize: "18px",
    fontWeight: "700",
    color: "#0c4a6e",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },
  featureCard: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  featureIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #0b4064 0%, #1e88e5 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    marginBottom: "16px",
  },
  featureTitle: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a365d",
  },
  featureDescription: {
    margin: "0",
    fontSize: "14px",
    color: "#475569",
    lineHeight: "1.5",
  },
  managementSection: {
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px dashed #cbd5e1",
  },
  managementTitle: {
    margin: "0 0 16px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a365d",
  },
  featureList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px",
  },
  featureItem: {
    fontSize: "14px",
    color: "#475569",
    padding: "10px 14px",
    background: "rgba(255, 255, 255, 0.7)",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  checkmark: {
    color: "#059669",
    fontWeight: "bold",
    fontSize: "16px",
  },
};

// Add CSS animation for spinner in a proper way
const addSpinnerAnimation = () => {
  if (typeof document !== "undefined") {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .feature-card-hover:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 25px rgba(11, 64, 100, 0.1);
      }
    `;
    document.head.appendChild(style);
  }
};

// Call the function to add animation
addSpinnerAnimation();

export default GenerateSellerQR;
